/* import __COLOCATED_TEMPLATE__ from './blaster.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { ACTION } from 'sh-www/models/action';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import clsx from 'clsx';

const CLASS_NAME = 'SH-c-admin-blaster';

const SECTIONS = {
  SEARCH: 'search',
  HISTORY: 'history',
};

const SECTION_TITLES = {
  SEARCH: 'Blaster Tool',
  HISTORY: 'History',
};

export const ADMIN_BLASTER_COMPONENT = {
  SECTIONS,
  SECTION_TITLES,
};

export default class AdminBlaster extends Component {
  get classNames() {
    return clsx(
      CLASS_NAME,
      'LSG-l-container-page LSG-l-container-page--medium-and-down-full-width',
      this.args.class,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get rateTypeText() {
    return this.args.posting.rateType === ASSIGNMENT.RATE_TYPE.FLAT ? 'Flat Rate' : 'Day Rate';
  }

  get activeSection() {
    return this.args.section || SECTIONS.SEARCH;
  }

  #uniqueBlastedUserIdsCache = createCache(() => {
    const { blastedUsers } = this.args;
    const userIdSet = new Set([...blastedUsers.map(({ id }) => id)]);

    return Array.from(userIdSet);
  });

  get uniqueBlastedUserIds() {
    return getValue(this.#uniqueBlastedUserIdsCache);
  }

  get allNotifiedCount() {
    return this.uniqueBlastedUserIds.length;
  }

  /**
   * Applied includes users who responded to the blast with an application.
   * This excludes users who have been hired.
   */
  get blastHistoryCountApplied() {
    return this.args.blastedUsersApplications.reduce((count, { status }) => {
      if (status >= 0 && status < 3) {
        count += 1;
      }

      return count;
    }, 0);
  }

  /**
   * Map the user id to the assignment user meta.
   * This person may not have been blasted.
   */
  get userIdToAssignmentUserMetaMap() {
    return this.args.posting.assignmentUserMetas.reduce((accum, assignmentUserMeta) => {
      accum[assignmentUserMeta.user.id] = assignmentUserMeta;

      return accum;
    }, {});
  }

  /**
   * Opted out includes users with no application that marked not interested.
   * This includes applications 'declined' by withdraw.
   */
  get blastHistoryCountOptedOut() {
    const { blastedUsersApplications } = this.args;
    const applicationIdToWithdrawnApplication = this.#applicationIdToWithdrawnApplication;

    const notInterestedCount = Object
      .values(this.#blastedUserIdToAssignmentUserMetaMap)
      .reduce((count, { notInterested }) => notInterested ? count + 1 : count, 0);

    // categorize withdrawn applications here too
    return blastedUsersApplications.reduce((count, application) => {
      const optedOut = applicationIdToWithdrawnApplication[application.id];
      if (application.status === "-1" && optedOut) {
        count += 1;
      }

      return count;
    }, notInterestedCount);
  }

  /**
   * Declined includes every application with status of 'declined'.
   * This excludes applications 'declined' by withdraw.
   */
  get blastHistoryCountDeclined() {
    const applicationIdToWithdrawnApplication = this.#applicationIdToWithdrawnApplication;

    return this.args.blastedUsersApplications.reduce((count, application) => {
      const optedOut = applicationIdToWithdrawnApplication[application.id];
      if (application.status < 0 && !optedOut) {
        count += 1;
      }

      return count;
    }, 0);
  }

  /**
   * Hired includes users who applied and were hired by the buyer.
   */
  get blastHistoryCountHired() {
    return this.args.blastedUsersApplications.reduce((count, { status }) => {
      if (status >= 3) {
        count += 1;
      }

      return count;
    }, 0);
  }

  get blastHistoryCountOutstanding() {
    return this.uniqueBlastedUserIds.length 
      - this.blastHistoryCountApplied 
      - this.blastHistoryCountOptedOut 
      - this.blastHistoryCountDeclined 
      - this.blastHistoryCountHired;
  }

  /** @type {Ember.A<Object>} The items for the subnav menu. */
  get navItems() {
    const navItems = [];

    const isNavActive = (section) => this.activeSection === section;

    Object.keys(ADMIN_BLASTER_COMPONENT.SECTIONS).forEach((section) => {
      navItems.push({
        title: SECTION_TITLES[section],
        isActive: () => isNavActive(SECTIONS[section]),
      });
    });

    return navItems;
  }

  get #applicationIdToActions() {
    return this.args.blastedUsersApplications.reduce((accum, { actions }) => {
      accum = accum.concat(actions.toArray());
      return accum;
    }, []);
  }

  get #applicationIdToWithdrawnApplication() {
    const applicationIdToActions = this.#applicationIdToActions;

    return applicationIdToActions.reduce((accum, action) => {
      if (action.name === ACTION.NAMES.PROJECT_WITHDRAW) {
        accum[action.actionable.id] = action.actionable;
      }

      return accum;
    }, {});
  }

  /**
   * Map blasted user id to the assignment user meta.
   */
  get #blastedUserIdToAssignmentUserMetaMap() {
    const { userIdToAssignmentUserMetaMap } = this;

    return Object.values(this.uniqueBlastedUserIds).reduce((accum, uniqueBlastedUserId) => {
      const assignmentUserMeta = userIdToAssignmentUserMetaMap[uniqueBlastedUserId];
      if (assignmentUserMeta) {
        accum[uniqueBlastedUserId] = assignmentUserMeta;
      }

      return accum;
    }, {});
  }

  @action
  onNavItemClicked(sectionTitle) {
    const section = Object.keys(SECTION_TITLES).find((section) => {
      return sectionTitle === SECTION_TITLES[section];
    })

    this.args.changeSection(SECTIONS[section]);
  }
}
